/* ================================
  media-gallery-swiper.js (Premium - STABLE + Arrow availability + single-media chrome guard)
  - Main uses Swiper (waits safely if Swiper loads later)
  - Thumbs remain native scroll (no Swiper)
  - Desktop/tablet thumbnail rail never exceeds main media height
  - Thumbnail rail dynamically follows the actual .pg-main height
  - hide_variants:
      • Only applies to "variant images" (data-variant-image)
      • We keep ONE "active variant media" visible at all times
      • Browsing other images never hides/reorders variant image
  - Variant change calls onVariantChange(activeVariantMediaId)
  - Desktop edge zones (15%), rAF throttled
  - Desktop hover arrows only show when Swiper can actually go prev/next
  - Premium fix:
      • If visible media count is less than 2, hide pg-thumbs / nav / cursor
      • Safe both on initial paint and after hide_variants / variant switching
  - No parallax
================================ */

/**
 * Wait for Swiper to exist on window (handles Swiper moved to bottom / conditional loads).
 * Resolves null if it doesn't show up within timeout.
 */
function k9WaitForSwiper(timeoutMs = 2500) {
  if (window.Swiper) return Promise.resolve(window.Swiper);

  return new Promise((resolve) => {
    const start = performance.now();

    const tick = () => {
      if (window.Swiper) return resolve(window.Swiper);
      if (performance.now() - start > timeoutMs) return resolve(null);
      requestAnimationFrame(tick);
    };

    tick();
  });
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.mainSwiper = null;
        this.mediaIdToIndex = new Map();

        this.activeVariantMediaId = null; // current variant’s featured media-id (sectionId-mediaId)

        this.onThumbClick = this.onThumbClick.bind(this);
        this.onMainSlideChange = this.onMainSlideChange.bind(this);

        this.onViewerMouseMove = this.onViewerMouseMove.bind(this);
        this.onViewerMouseLeave = this.onViewerMouseLeave.bind(this);
        this.onViewerClickDesktop = this.onViewerClickDesktop.bind(this);

        this._rafMove = 0;
        this._lastMouseEvent = null;

        this._rebuildToken = 0; // protects against duplicate async rebuilds
        this._pgViewer = null;

        // Keeps desktop/tablet thumb rail matched to actual main viewer height.
        this._thumbHeightObserver = null;
        this._thumbHeightRaf = 0;
        this._syncThumbHeight = this.syncThumbHeight.bind(this);
      }

      connectedCallback() {
        // queue microtask: avoids edge cases where inner markup is still settling
        queueMicrotask(() => {
          this.bindThumbHeightSync();
          this.rebuild();
        });

        this.addEventListener('click', this.onThumbClick);
        this.bindDesktopViewerUX();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onThumbClick);
        this.unbindDesktopViewerUX();
        this.unbindThumbHeightSync();
        this.destroy();
      }

      /* ---------------------------------
        Hooks used by product-info.js
      ---------------------------------- */

      // product-info.js should call this on variant change
      onVariantChange(variantMediaId) {
        if (!variantMediaId) return;

        this.activeVariantMediaId = variantMediaId;

        if (this.hideVariantsEnabled()) {
          this.applyHideVariants();            // hide OTHER variant images (based on activeVariantMediaId)
          this.promoteActiveVariantToFront();  // keep active variant first (only on variant change)
        }

        this.setActiveMedia(variantMediaId, true);
      }

      // Normal browsing (thumb click / swipe) should NOT change variant hiding or DOM order
      setActiveMedia(targetMediaId, shouldScrollThumb = true) {
        if (!targetMediaId) return;

        this.buildIndexMap();
        const index = this.mediaIdToIndex.get(targetMediaId);
        if (index == null) return;

        this.preloadAndDecode(targetMediaId);

        if (this.mainSwiper) {
          this.mainSwiper.slideTo(index, this.getMotionSpeed());
          this.updateNavAvailability();
        } else {
          const slide = this.querySelector(
            `.pg-main .pg-slide[data-media-id="${CSS.escape(targetMediaId)}"]`
          );
          slide?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }

        this.setThumbCurrent(targetMediaId, shouldScrollThumb);
      }

      /* ---------------------------
        Init / Rebuild
      ---------------------------- */

      async rebuild() {
        const token = ++this._rebuildToken;

        this.destroy();
        this.buildIndexMap();

        // Wait for Swiper safely (in case Swiper JS is loaded later / conditionally)
        const SwiperCtor = await k9WaitForSwiper();
        if (token !== this._rebuildToken) return; // a newer rebuild started

        if (SwiperCtor) {
          this.initMainSwiper(SwiperCtor);
        }

        this.syncGalleryChrome();

        // Determine initial "active variant media" from aria-current thumb
        const currentBtn = this.querySelector('.pg-thumbs .pg-tbtn[aria-current="true"]');
        const initialTarget = currentBtn?.closest('.pg-thumb')?.dataset?.target || null;

        // If we can’t find it, fallback to first slide media-id
        const firstSlideId =
          this.querySelector('.pg-main .pg-slide[data-media-id]')?.dataset?.mediaId || null;

        this.activeVariantMediaId = initialTarget || firstSlideId;

        if (this.hideVariantsEnabled() && this.activeVariantMediaId) {
          this.applyHideVariants();
          this.promoteActiveVariantToFront();
        }

        // Show initial target
        if (initialTarget) this.setActiveMedia(initialTarget, false);
        else if (firstSlideId) this.setActiveMedia(firstSlideId, false);

        this.syncGalleryChrome();
        this.syncThumbHeight();
      }

      destroy() {
        if (this.mainSwiper) {
          this.mainSwiper.off('slideChange', this.onMainSlideChange);
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }

        this.mediaIdToIndex.clear();
      }

      buildIndexMap() {
        this.mediaIdToIndex.clear();

        // Only slides Swiper should see (must have swiper-slide)
        const slides = Array.from(
          this.querySelectorAll('.pg-main .swiper-slide.pg-slide[data-media-id]')
        );

        slides.forEach((slide, idx) => {
          this.mediaIdToIndex.set(slide.dataset.mediaId, idx);
        });
      }

      getMotionSpeed() {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        return reduce ? 0 : 620;
      }

      getVisibleMediaCount() {
        return this.querySelectorAll('.pg-main .pg-slide[data-media-id]:not([hidden])').length;
      }

      /* ---------------------------
        Thumbnail rail height
      ---------------------------- */

      syncThumbHeight() {
        if (this._thumbHeightRaf) {
          cancelAnimationFrame(this._thumbHeightRaf);
        }

        this._thumbHeightRaf = requestAnimationFrame(() => {
          this._thumbHeightRaf = 0;

          // Mobile uses horizontal thumbnails and needs no vertical constraint.
          if (window.matchMedia('(max-width: 749px)').matches) {
            this.style.removeProperty('--prg-main-height');
            return;
          }

          const main = this.querySelector('.pg-main');

          if (!main) {
            this.style.removeProperty('--prg-main-height');
            return;
          }

          const height = Math.round(main.getBoundingClientRect().height);

          if (height > 0) {
            this.style.setProperty('--prg-main-height', `${height}px`);
          }
        });
      }

      bindThumbHeightSync() {
        this.unbindThumbHeightSync();

        const main = this.querySelector('.pg-main');
        if (!main) return;

        if ('ResizeObserver' in window) {
          this._thumbHeightObserver = new ResizeObserver(() => {
            this.syncThumbHeight();
          });

          this._thumbHeightObserver.observe(main);
        }

        // Also covers orientation / breakpoint changes.
        window.addEventListener('resize', this._syncThumbHeight, { passive: true });

        this.syncThumbHeight();
      }

      unbindThumbHeightSync() {
        if (this._thumbHeightObserver) {
          this._thumbHeightObserver.disconnect();
          this._thumbHeightObserver = null;
        }

        window.removeEventListener('resize', this._syncThumbHeight);

        if (this._thumbHeightRaf) {
          cancelAnimationFrame(this._thumbHeightRaf);
          this._thumbHeightRaf = 0;
        }

        this.style.removeProperty('--prg-main-height');
      }

      syncGalleryChrome() {
        const visibleCount = this.getVisibleMediaCount();
        const isSingle = visibleCount < 2;

        this.classList.toggle('pg--single-media', isSingle);

        const thumbs = this.querySelector('.pg-thumbs');
        const prev = this.querySelector('.pg-prev');
        const next = this.querySelector('.pg-next');
        const cursor = this.querySelector('.pg-cursor');

        if (thumbs) thumbs.hidden = isSingle;
        if (prev) prev.hidden = isSingle;
        if (next) next.hidden = isSingle;
        if (cursor) cursor.hidden = isSingle;

        if (isSingle) {
          this.dataset.canPrev = 'false';
          this.dataset.canNext = 'false';

          this.classList.remove('pg--hovering', 'pg--hover-left', 'pg--hover-right');
          this.style.setProperty('--pg-cx', `-9999px`);
          this.style.setProperty('--pg-cy', `-9999px`);

          this.unbindDesktopViewerUX();
          return;
        }

        this.updateNavAvailability();
        this.bindDesktopViewerUX();
      }

      initMainSwiper(SwiperCtor) {
        // IMPORTANT: this should be the Swiper "container" element that holds the wrapper inside.
        // Your markup should look like:
        // <div class="pg-main swiper" id="GalleryViewer-...">
        //   <div class="pg-main-track swiper-wrapper"> ...slides... </div>
        // </div>
        const viewer =
          this.querySelector('[id^="GalleryViewer-"].pg-main') || this.querySelector('.pg-main');

        if (!viewer) return;

        const prev = this.querySelector('.pg-prev');
        const next = this.querySelector('.pg-next');

        this.mainSwiper = new SwiperCtor(viewer, {
  slidesPerView: 1,
  spaceBetween: 0,
  roundLengths: true,

  speed: 480,

  watchOverflow: true,
  loop: false,

  resistance: true,
  resistanceRatio: 0.65,

  threshold: 5,
  touchAngle: 35,
  touchRatio: 1,

  shortSwipes: true,
  longSwipes: true,
  longSwipesRatio: 0.25,
  longSwipesMs: 280,

  followFinger: true,

  simulateTouch: true,
  allowTouchMove: true,
  grabCursor: false,

  touchStartPreventDefault: false,

  lazyPreloadPrevNext: 1,

  parallax: false,

  navigation: {
    prevEl: prev,
    nextEl: next,
  },

  on: {
    slideChange: this.onMainSlideChange,
  },
});

        this.syncThumbHeight();
      }

      refreshSwiper() {
        if (this.mainSwiper) {
          this.mainSwiper.updateSlides();
          this.mainSwiper.update();
        }

        this.syncGalleryChrome();
        this.syncThumbHeight();
      }

      /* ---------------------------
        Nav availability (prevents left arrow on first slide, etc.)
      ---------------------------- */

      updateNavAvailability() {
        const swiper = this.mainSwiper;

        if (!swiper) {
          this.dataset.canPrev = 'true';
          this.dataset.canNext = 'true';
          return;
        }

        const canPrev = !swiper.isBeginning;
        const canNext = !swiper.isEnd;

        this.dataset.canPrev = canPrev ? 'true' : 'false';
        this.dataset.canNext = canNext ? 'true' : 'false';

        if (!canPrev) this.classList.remove('pg--hover-left');
        if (!canNext) this.classList.remove('pg--hover-right');

        if (!canPrev && !canNext) {
          this.classList.remove('pg--hovering');
          this.style.setProperty('--pg-cx', `-9999px`);
          this.style.setProperty('--pg-cy', `-9999px`);
        }
      }

      /* ---------------------------
        Premium hide_variants (stable)
      ---------------------------- */

      hideVariantsEnabled() {
        return String(this.dataset.hideVariants) === 'true';
      }

      applyHideVariants() {
        const keepId = this.activeVariantMediaId;
        if (!keepId) return;

        // MAIN: hide all variant slides except activeVariantMediaId
        const slides = Array.from(
          this.querySelectorAll('.pg-main .pg-slide[data-media-id]')
        );

        slides.forEach((s) => {
          const isVariant = s.hasAttribute('data-variant-image');
          const shouldHide = isVariant && s.dataset.mediaId !== keepId;

          s.toggleAttribute('hidden', shouldHide);
          s.classList.toggle('pg-is-hidden', shouldHide);

          // Exclude from Swiper if hidden
          if (shouldHide) {
            s.classList.remove('swiper-slide');
            s.setAttribute('aria-hidden', 'true');
            s.setAttribute('tabindex', '-1');
          } else {
            s.classList.add('swiper-slide');
            s.removeAttribute('aria-hidden');
            s.removeAttribute('tabindex');
          }
        });

        // THUMBS: hide all variant thumbs except activeVariantMediaId
        const thumbs = Array.from(
          this.querySelectorAll('.pg-thumbs .pg-thumb[data-target]')
        );

        thumbs.forEach((t) => {
          const isVariant = t.hasAttribute('data-variant-image');
          t.hidden = isVariant && t.dataset.target !== keepId;
        });

        this.refreshSwiper();
      }

      promoteActiveVariantToFront() {
        const keepId = this.activeVariantMediaId;
        if (!keepId) return;

        const mainTrack = this.querySelector('.pg-main-track');
        const thumbs = this.querySelector('.pg-thumbs');
        const thumbsTrack = this.querySelector('.pg-thumbs-track');

        if (!mainTrack) return;

        // Preserve both mobile horizontal and desktop/tablet vertical thumb position.
        const prevScrollLeft = thumbs ? thumbs.scrollLeft : 0;
        const prevScrollTop = thumbs ? thumbs.scrollTop : 0;

        const targetSlide = mainTrack.querySelector(
          `.pg-slide[data-media-id="${CSS.escape(keepId)}"]`
        );

        if (targetSlide && mainTrack.firstElementChild !== targetSlide) {
          mainTrack.insertBefore(targetSlide, mainTrack.firstElementChild);
        }

        if (thumbsTrack) {
          const targetThumb = thumbsTrack.querySelector(
            `.pg-thumb[data-target="${CSS.escape(keepId)}"]`
          );

          if (targetThumb && thumbsTrack.firstElementChild !== targetThumb) {
            thumbsTrack.insertBefore(targetThumb, thumbsTrack.firstElementChild);
          }
        }

        if (thumbs) {
          thumbs.scrollLeft = prevScrollLeft;
          thumbs.scrollTop = prevScrollTop;
        }

        this.refreshSwiper();
      }

      preloadAndDecode(targetMediaId) {
        const slide = this.querySelector(
          `.pg-main .pg-slide[data-media-id="${CSS.escape(targetMediaId)}"]`
        );

        const img = slide?.querySelector('img.pg-img');
        if (!img) return;

        try {
          img.loading = 'eager';

          const isLcp = img.getAttribute('data-lcp') === 'true';
          img.fetchPriority = isLcp ? 'high' : 'auto';
        } catch (_) {}

        if (img.decode) img.decode().catch(() => {});
      }

      /* ---------------------------
        Thumb click handling
      ---------------------------- */

      onThumbClick(e) {
        const btn = e.target.closest('.pg-tbtn');
        if (!btn) return;

        const thumb = btn.closest('.pg-thumb');
        const targetMediaId = thumb?.dataset?.target;
        if (!targetMediaId) return;

        e.preventDefault();
        this.setActiveMedia(targetMediaId, true);
      }

      onMainSlideChange() {
        if (!this.mainSwiper) return;

        this.updateNavAvailability();

        const activeSlide = this.mainSwiper.slides?.[this.mainSwiper.activeIndex];
        const mediaId = activeSlide?.dataset?.mediaId;
        if (!mediaId) return;

        this.setThumbCurrent(mediaId, true);
      }

      /* ---------------------------
        aria-current + thumb scroll
      ---------------------------- */

      setThumbCurrent(targetMediaId, shouldScrollThumb) {
        const buttons = this.querySelectorAll('.pg-thumbs .pg-tbtn');
        buttons.forEach((b) => b.removeAttribute('aria-current'));

        const activeThumb = this.querySelector(
          `.pg-thumbs .pg-thumb[data-target="${CSS.escape(targetMediaId)}"]`
        );

        const activeBtn = activeThumb?.querySelector('.pg-tbtn');

        if (activeBtn) {
          activeBtn.setAttribute('aria-current', 'true');
        }

        if (shouldScrollThumb && activeThumb) {
          activeThumb.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
          });
        }
      }

      /* =========================================================
        Desktop edge zones (15%, rAF throttled)
        - Only shows hover arrow if Swiper can go that way
      ========================================================== */

      bindDesktopViewerUX() {
        if (!this.classList.contains('pg--arrows-desktop')) return;
        if (this.getVisibleMediaCount() < 2) return;
        if (this._pgViewer) return;

        const viewer =
          this.querySelector('[id^="GalleryViewer-"].pg-main') || this.querySelector('.pg-main');

        if (!viewer) return;

        this._pgViewer = viewer;

        // pointer events are more reliable across devices/browsers
        viewer.addEventListener('pointermove', this.onViewerMouseMove, { passive: true });
        viewer.addEventListener('pointerleave', this.onViewerMouseLeave);
        viewer.addEventListener('click', this.onViewerClickDesktop);
      }

      unbindDesktopViewerUX() {
        const viewer = this._pgViewer;
        if (!viewer) return;

        viewer.removeEventListener('pointermove', this.onViewerMouseMove);
        viewer.removeEventListener('pointerleave', this.onViewerMouseLeave);
        viewer.removeEventListener('click', this.onViewerClickDesktop);

        this._pgViewer = null;

        this.classList.remove('pg--hovering', 'pg--hover-left', 'pg--hover-right');
        this.style.setProperty('--pg-cx', `-9999px`);
        this.style.setProperty('--pg-cy', `-9999px`);

        if (this._rafMove) cancelAnimationFrame(this._rafMove);

        this._rafMove = 0;
        this._lastMouseEvent = null;
      }

      onViewerMouseMove(e) {
        this._lastMouseEvent = e;
        if (this._rafMove) return;

        this._rafMove = requestAnimationFrame(() => {
          this._rafMove = 0;

          const viewer = this._pgViewer;
          const ev = this._lastMouseEvent;

          if (!viewer || !ev) return;

          // ensure dataset canPrev/canNext is current
          this.updateNavAvailability();

          const canPrev = this.dataset.canPrev !== 'false';
          const canNext = this.dataset.canNext !== 'false';

          const r = viewer.getBoundingClientRect();
          const x = ev.clientX - r.left;
          const y = ev.clientY - r.top;

          const edgeRatio = 0.15;
          const edge = r.width * edgeRatio;

          const inLeftRaw = x <= edge;
          const inRightRaw = x >= r.width - edge;

          const inLeft = inLeftRaw && canPrev;
          const inRight = inRightRaw && canNext;

          if (!inLeft && !inRight) {
            this.classList.remove('pg--hovering', 'pg--hover-left', 'pg--hover-right');
            this.style.setProperty('--pg-cx', `-9999px`);
            this.style.setProperty('--pg-cy', `-9999px`);
            return;
          }

          this.style.setProperty('--pg-cx', `${x}px`);
          this.style.setProperty('--pg-cy', `${y}px`);

          this.classList.add('pg--hovering');
          this.classList.toggle('pg--hover-left', inLeft);
          this.classList.toggle('pg--hover-right', inRight);

          // Arrow rotation: right = -45deg, left = 135deg
          this.style.setProperty('--pg-rot', inLeft ? '135deg' : '-45deg');
        });
      }

      onViewerMouseLeave() {
        this.classList.remove('pg--hovering', 'pg--hover-left', 'pg--hover-right');
        this.style.setProperty('--pg-cx', `-9999px`);
        this.style.setProperty('--pg-cy', `-9999px`);

        if (this._rafMove) cancelAnimationFrame(this._rafMove);

        this._rafMove = 0;
        this._lastMouseEvent = null;
      }

      onViewerClickDesktop(e) {
        if (e.target.closest('button, a, .pg-nav')) return;

        const viewer = this._pgViewer;
        if (!viewer || !this.mainSwiper) return;

        this.updateNavAvailability();

        const canPrev = this.dataset.canPrev !== 'false';
        const canNext = this.dataset.canNext !== 'false';

        const r = viewer.getBoundingClientRect();
        const x = e.clientX - r.left;

        const edgeRatio = 0.15;
        const edge = r.width * edgeRatio;

        const inLeft = x <= edge;
        const inRight = x >= r.width - edge;

        if (inLeft && canPrev) {
          this.mainSwiper.slidePrev(this.getMotionSpeed());
        } else if (inRight && canNext) {
          this.mainSwiper.slideNext(this.getMotionSpeed());
        }
      }
    }
  );
}